import { RHCompanyRelationshipKind, RHCompanyRelationshipPolicy } from "../../SocketClientFunctions"


const RELATIONSHIP_KIND_LABELS: Record<RHCompanyRelationshipKind, string> = {
    "parent-child": "Ema-tütar suhe",
    "delegated-administration": "Delegeeritud haldusõigus",
    "procurement-service": "Hankekorraldusteenus",
    "shared-service": "Jagatud teenus"
}


// The single authority translating RHCompanyRelationshipPolicy's boolean fields
// into plain-language Estonian sentences — mirrors CompanyRoleBadgesView's role
// of being the one place that owns a display-label vocabulary. Used everywhere
// a relationship's granted authority must be shown to a user, so nobody ever
// reads a raw camelCase field name (e.g. in the relationship-request e-mail).
export class CompanyRelationshipPolicyLabels {

    static labelForKind(kind: RHCompanyRelationshipKind): string {
        return RELATIONSHIP_KIND_LABELS[kind] ?? kind
    }


    // Every capability below is fixed-direction ("emaettevõte" is always the grantee acting on
    // "tütarettevõte", per the *ForChild/Child*/canSeeChildExists field naming) regardless of which
    // side (parent or child) is currently viewing the page, so the subject/object is spelled out
    // explicitly in each label instead of leaving it to the reader to infer from the field name.
    static activeLabelsForPolicy(policy: RHCompanyRelationshipPolicy): string[] {
        const labels: string[] = []

        if (policy.canSeeChildExists) {
            labels.push("Emaettevõte näeb, et see tütarettevõte on temaga seotud")
        }
        if (policy.canViewConsolidatedReports) {
            labels.push("Emaettevõte näeb tütarettevõtte koondaruandeid (ilma detailideta)")
        }
        if (policy.canManageChildUsers || policy.canInviteRelatedCompanyEmployees) {
            labels.push("Emaettevõte võib hallata ja kutsuda tütarettevõtte liikmeid")
        }
        if (
            policy.canCreateProcurementsForChild ||
            policy.canManageProcurementsForChild ||
            policy.canViewChildProcurementPublicData ||
            policy.canViewChildProcurementDetailedData
        ) {
            labels.push("Emaettevõte võib luua ja hallata tütarettevõtte hankeid")
        }
        if (policy.canViewChildConfidentialOffers) {
            labels.push("Emaettevõte näeb tütarettevõtte konfidentsiaalseid pakkumusi")
        }

        if (policy.canTransferEmployeesToChildren) {
            labels.push("Lubab töötajate üleviimist emaettevõttest tütarettevõttesse")
        }
        if (policy.canTransferEmployeesToParent) {
            labels.push("Lubab töötajate üleviimist tütarettevõttest emaettevõttesse")
        }
        if (policy.canTransferEmployeesBetweenSiblings) {
            labels.push("Lubab töötajate üleviimist sõsarettevõtete vahel (sama emaettevõtte teiste tütarettevõtete vahel)")
        }

        const anyTransferAllowed =
            policy.canTransferEmployeesToChildren ||
            policy.canTransferEmployeesToParent ||
            policy.canTransferEmployeesBetweenSiblings
        if (anyTransferAllowed && policy.requiresSourceAdminApprovalForTransfers) {
            labels.push("Tingimus: töötaja üleviimine eeldab lähteettevõtte peakasutaja kinnitust")
        }
        if (anyTransferAllowed && policy.requiresTargetAdminApprovalForTransfers) {
            labels.push("Tingimus: töötaja üleviimine eeldab sihtettevõtte peakasutaja kinnitust")
        }
        if (anyTransferAllowed && policy.requiresUserConsentForTransfers) {
            labels.push("Tingimus: töötaja üleviimine eeldab töötaja enda nõusolekut")
        }

        if (labels.length === 0) {
            labels.push("Õigusi ei ole antud")
        }
        return labels
    }

}
